import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet } from 'react-router-dom';
import { ErrorBoundary } from '@/components/errorBoundary';
import { OnboardingGateSkeleton } from './OnboardingGateSkeleton';
import {
    useOnboardingGate,
    type UseOnboardingGateResult,
} from './useOnboardingGate';

/**
 * Wraps every secured route (mounted by `SecuredRoutes`): an organisation that
 * has not finished onboarding sends its OrgAdmin to the wizard and everyone
 * else to the setup-in-progress page; an onboarded one closes both. The table
 * is `decideOnboardingRoute`.
 *
 * Until the organisation is read it shows a skeleton, and a failed read shows
 * the error fallback with Retry. Neither has the shell: `useLayout` wraps a
 * signed-in route only once the organisation is known to be onboarded.
 */
export const OnboardingGate: React.FC = () => {
    const gate = useOnboardingGate();
    const { t } = useTranslation('pages');

    return (
        <ErrorBoundary
            data-testid="onboarding-gate-error-boundary"
            fallbackMessage={t('onboarding.gate.errorMessage')}
            id="onboarding-gate-error-boundary"
            onRetry={gate.onRetry}
            resetKeys={[gate.error]}
            retryLabel={t('onboarding.gate.errorRetry')}
            title={t('onboarding.gate.errorTitle')}
        >
            <OnboardingGateContent gate={gate} />
        </ErrorBoundary>
    );
};

const OnboardingGateContent: React.FC<{ gate: UseOnboardingGateResult }> = ({
    gate,
}) => {
    if (gate.error) {
        throw gate.error;
    }

    if (gate.decision === null) {
        return <OnboardingGateSkeleton />;
    }

    if (gate.decision !== 'render') {
        return <Navigate replace to={gate.decision.redirect} />;
    }

    return <Outlet />;
};
